import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, useRouteRefParams } from '@backstage/frontend-plugin-api';
import { catalogApiRef, entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  AccountEntity,
  CloudResourceEntity,
  getAccountSyncState,
  relTime,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { AccountScanInfo, ResourceErrorsAlert } from '../Messages';
import {
  RulesContextProvider,
  useAccountContext,
} from '../Providers';
import { PageSkeleton } from '@internal/backstage-plugin-devops-react';
import {
  AlertsChartPeriod,
  AlertsChartType,
  AlertsStatisticsResponse,
  SpmAlertInfo,
  getTimePeriodInDays,
} from '@internal/backstage-plugin-devops-spm-common';
import { spmApiRef } from '@internal/backstage-plugin-devops-spm';
import styles from './overview-page.module.css';

const CATEGORIES = ['Security', 'Finance', 'Housekeeping'] as const;

/** Category accent class for the compliance snapshot bars (mockup). */
const catAccentClass = (category: string): string => {
  if (category === 'Security') {
    return styles.catAccentSecurity;
  }
  if (category === 'Finance') {
    return styles.catAccentFinance;
  }
  return styles.catAccentHousekeeping;
};

const RING_CIRCUMFERENCE = 226.2;

const ruleTitle = (rule: RuleEntity) =>
  rule.metadata.title ?? rule.metadata.name;

/** Category abbreviation chip for inventory rows (ST/NE/SE/CO…). */
const abbrOf = (category: string) =>
  category
    .split(/[\s&]+/)
    .filter(Boolean)
    .map(word => word[0]!.toUpperCase())
    .join('')
    .slice(0, 2);

function KpiCard(props: {
  to: string;
  label: string;
  value: ReactNode;
  sub: string;
}) {
  const { to, label, value, sub } = props;
  return (
    <Link to={to} className={`${styles.card} ${styles.kpi}`}>
      <span className={styles.k}>{label}</span>
      <span className={styles.v}>{value}</span>
      <span className={styles.sub}>{sub}</span>
    </Link>
  );
}

/** Alerts mini-chart: last 14 daily totals as columns (mockup .al-chart). */
function AlertsMiniChart({
  statistics,
}: {
  statistics: AlertsStatisticsResponse[];
}) {
  const tail = statistics.slice(-14);
  const max = Math.max(1, ...tail.map(row => row.totalAlertsCount));
  return (
    <div className={styles.alChart}>
      {tail.map((row, index) => (
        <div
          key={row.date}
          className={`${styles.alCol} ${index === tail.length - 1 ? styles.alColCur : ''}`}
          title={`${row.date}: ${row.totalAlertsCount} total`}
        >
          <i
            style={{
              height: `${Math.max(8, (row.totalAlertsCount / max) * 100)}%`,
            }}
          />
          <span>{row.date.slice(-2)}</span>
        </div>
      ))}
      {tail.length === 0 && (
        <div className={styles.cardSub}>No alerts data for this period</div>
      )}
    </div>
  );
}

interface ComplianceSnapshot {
  score: number;
  failedCount: number;
  rulesTotal: number;
  unknownCount: number;
  failedResources: number;
  failedChecks: number;
}

interface InventorySnapshot {
  total: number;
  services: number;
  regions: number;
  top: [string, number][];
  restCount: number;
  restSum: number;
  regionList: string[];
}

const AccountOverviewTabContent = () => {
  const { name } = useRouteRefParams(entityRouteRef);
  const { entity } = useEntity();
  const account = entity as AccountEntity;
  const catalogApi = useApi(catalogApiRef);
  const spmApi = useApi(spmApiRef);
  const { isLoading } = useAccountContext();

  const syncState = getAccountSyncState(account);

  const [rules, setRules] = useState<RuleEntity[]>([]);
  const ruleByRuleId = useMemo(
    () => new Map(rules.map(rule => [rule.metadata.name, rule])),
    [rules],
  );
  const [resources, setResources] = useState<CloudResourceEntity[]>([]);
  const [statistics, setStatistics] = useState<AlertsStatisticsResponse[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<SpmAlertInfo[]>([]);
  const [activeFindings, setActiveFindings] = useState<number | undefined>(
    undefined,
  );
  const resourceById = useMemo(
    () => new Map(resources.map(resource => [resource.metadata.name, resource])),
    [resources],
  );

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Rule', 'spec.account': name } })
      .then(({ items }) => {
        if (!cancelled) {
          setRules(items as RuleEntity[]);
        }
      })
      .catch(() => {
        // Overview stays usable without the compliance snapshot numbers.
      });
    catalogApi
      .getEntities({
        filter: { kind: 'CloudResource', 'spec.account': name },
      })
      .then(({ items }) => {
        if (!cancelled) {
          setResources(items as CloudResourceEntity[]);
        }
      })
      .catch(() => {
        // Overview stays usable without the inventory snapshot numbers.
      });
    catalogApi
      .getEntities({
        filter: {
          kind: 'Insight',
          'spec.type': 'ExternalAccess',
          'spec.account': name,
        },
      })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        setActiveFindings(
          items.filter(
            insight => (insight.spec as { status?: string }).status === 'Active',
          ).length,
        );
      })
      .catch(() => {
        // Insights card hides when catalog has no analyzer data.
      });
    spmApi
      .getAlertStatistics(
        name,
        getTimePeriodInDays(AlertsChartPeriod.LAST_MONTH),
        1,
        [AlertsChartType.TOTAL],
      )
      .then(([alertsStatistics]) => {
        if (!cancelled) {
          setStatistics(alertsStatistics);
        }
      })
      .catch(() => {
        // Alerts card hides its chart when SPM has no data.
      });
    spmApi
      .getRecentAlerts(name, 5)
      .then(alerts => {
        if (!cancelled) {
          setRecentAlerts(alerts);
        }
      })
      .catch(() => {
        // Alerts card hides its list when SPM has no data.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, spmApi, name]);

  const compliance = useMemo<ComplianceSnapshot>(() => {
    const scored = rules.filter(rule => rule.spec.result !== 'Unknown');
    const passed = scored.filter(rule => rule.spec.result === 'Passed').length;
    const failed = rules.filter(rule => rule.spec.result === 'Failed');
    const failedResources = new Set(
      failed.flatMap(rule => rule.spec.resources.map(resource => resource.id)),
    );
    return {
      score: scored.length ? Math.round((passed / scored.length) * 100) : 0,
      failedCount: failed.length,
      rulesTotal: rules.length,
      unknownCount: rules.length - scored.length,
      failedResources: failedResources.size,
      failedChecks: failed.reduce(
        (sum, rule) => sum + rule.spec.resources.length,
        0,
      ),
    };
  }, [rules]);

  const byCategory = useMemo(
    () =>
      CATEGORIES.map(category => {
        const rows = rules.filter(rule => rule.spec.category === category);
        const passed = rows.filter(rule => rule.spec.result === 'Passed').length;
        return {
          category,
          passed,
          total: rows.length,
        };
      }).filter(row => row.total > 0),
    [rules],
  );

  const inventory = useMemo<InventorySnapshot>(() => {
    const categories = new Map<string, number>();
    const services = new Set<string>();
    const regions = new Set<string>();
    resources.forEach(resource => {
      const category = resource.spec?.type ?? 'Other';
      categories.set(category, (categories.get(category) ?? 0) + 1);
      services.add(resource.spec?.service ?? '');
      regions.add(resource.spec?.region ?? '');
    });
    regions.delete('');
    const sorted = [...categories.entries()].sort((a, b) => b[1] - a[1]);
    return {
      total: resources.length,
      services: services.size,
      regions: regions.size,
      top: sorted.slice(0, 4),
      restCount: Math.max(0, categories.size - 4),
      restSum: sorted.slice(4).reduce((sum, [, count]) => sum + count, 0),
      regionList: [...regions].slice(0, 3),
    };
  }, [resources]);

  const topFailedRules = useMemo(
    () =>
      rules
        .filter(rule => rule.spec.result === 'Failed')
        .sort((a, b) => b.spec.resources.length - a.spec.resources.length)
        .slice(0, 3),
    [rules],
  );

  const ringClass = () => {
    if (compliance.score >= 80) {
      return styles.ringSuccess;
    }
    if (compliance.score >= 50) {
      return styles.ringWarning;
    }
    return styles.ringDanger;
  };

  const maxInv = Math.max(1, ...inventory.top.map(([, count]) => count));
  const accountHref = `/catalog/default/account/${name}`;

  if (isLoading) {
    return <PageSkeleton rows={8} />;
  }

  return (
    <>
      {/* scan progress (self-hiding: renders only while a scan is running) */}
      <div className={styles.alertSlot}>
        <AccountScanInfo />
      </div>

      {/* KPI band (links into tabs) */}
      <div className={styles.kpiBand}>
        <KpiCard
          to={`${accountHref}/cloud-resources`}
          label="Inventory"
          value={
            <>
              {inventory.total}
              <small>resources</small>
            </>
          }
          sub={`${inventory.services} services · ${inventory.regions} regions`}
        />
        <KpiCard
          to={`${accountHref}/compliance`}
          label="Compliance score"
          value={
            <>
              {compliance.score}
              <small>/100</small>
            </>
          }
          sub="Unknown rules excluded"
        />
        <KpiCard
          to={`${accountHref}/compliance?st=Failed`}
          label="Failed rules"
          value={
            <>
              {compliance.failedCount}
              <small>
                of {compliance.rulesTotal}
                {compliance.unknownCount > 0
                  ? ` · ${compliance.unknownCount} unknown`
                  : ''}
              </small>
            </>
          }
          sub={`${compliance.failedChecks} failed checks on ${compliance.failedResources} resources`}
        />
      </div>

      {/* sync alert (muted-aware, same as before) */}
      <div className={styles.alertSlot}>
        <ResourceErrorsAlert />
      </div>

      {/* row: compliance snapshot + inventory snapshot */}
      <div className={styles.row2}>
        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Compliance snapshot
            <Link className={styles.go} to={`${accountHref}/compliance`}>
              Open Compliance →
            </Link>
          </div>
          <div className={styles.compBody}>
            <div className={styles.ring}>
              <svg width="84" height="84" viewBox="0 0 84 84">
                <circle
                  cx="42"
                  cy="42"
                  r="36"
                  fill="none"
                  stroke="var(--bui-bg-neutral-2)"
                  strokeWidth="8"
                />
                <circle
                  cx="42"
                  cy="42"
                  r="36"
                  fill="none"
                  className={ringClass()}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={
                    RING_CIRCUMFERENCE * (1 - compliance.score / 100)
                  }
                />
              </svg>
              <div className={styles.ringVal}>
                <div>
                  <b>{compliance.score}</b>
                  <span>score</span>
                </div>
              </div>
            </div>
            <div className={styles.catBars}>
              {byCategory.map(row => {
                const pct = row.total
                  ? Math.round((row.passed / row.total) * 100)
                  : 0;
                return (
                  <Link
                    key={row.category}
                    className={styles.catBar}
                    to={`${accountHref}/compliance?cat=${row.category}`}
                  >
                    <span className={styles.nm}>{row.category}</span>
                    <span className={styles.bar}>
                      <i
                        className={catAccentClass(row.category)}
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                    <span className={styles.pc}>
                      {row.passed}/{row.total}
                    </span>
                  </Link>
                );
              })}
              {byCategory.length === 0 && (
                <div className={styles.cardSub}>No rules ingested yet</div>
              )}
            </div>
          </div>
          <div className={styles.alList}>
            {topFailedRules.map(rule => (
              <div key={rule.metadata.name} className={styles.alItem}>
                <span className={styles.nm}>{ruleTitle(rule)}</span>
                <span className={styles.when}>
                  {rule.spec.resources.length} affected
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Inventory snapshot
            <Link className={styles.go} to={`${accountHref}/cloud-resources`}>
              Open Resources →
            </Link>
          </div>
          <div className={styles.cardSub}>
            {inventory.total} resources across {inventory.services} services
          </div>
          <div className={styles.invBars}>
            {inventory.top.map(([category, count]) => (
              <div key={category} className={styles.invRow}>
                <span className={styles.nm}>
                  <i style={{ background: 'var(--bui-accent-bg)' }}>
                    {abbrOf(category)}
                  </i>
                  {category}
                </span>
                <span className={styles.bar}>
                  <i style={{ width: `${(count / maxInv) * 100}%` }} />
                </span>
                <span className={styles.n}>{count}</span>
              </div>
            ))}
            {inventory.restCount > 0 && (
              <div className={styles.invRow}>
                <span className={styles.nm}>
                  + {inventory.restCount} more categories
                </span>
                <span className={styles.bar}>
                  <i style={{ width: '45%', opacity: 0.4 }} />
                </span>
                <span className={styles.n}>{inventory.restSum}</span>
              </div>
            )}
          </div>
          <div className={styles.invFoot}>
            <span>
              Regions: <b>{inventory.regionList.join(', ') || '—'}</b>
            </span>
            <span>
              Sync: <b>{syncState.label}</b>
            </span>
          </div>
        </div>
      </div>

      {/* row: alerts + insights */}
      <div className={styles.row2b}>
        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Alerts
            <Link className={styles.go} to={`${accountHref}/statistics`}>
              View all →
            </Link>
          </div>
          <AlertsMiniChart statistics={statistics} />
          <div className={styles.alList}>
            {recentAlerts.map(alert => {
              const rule = ruleByRuleId.get(alert.ruleId);
              const title = rule?.metadata.title ?? alert.metadata?.type ?? alert.ruleId;
              // Pretty resource line from the ingested entity; the raw id
              // stays available as tooltip and fallback.
              const resource = resourceById.get(alert.resourceId);
              const resLine = resource
                ? `${resource.spec.region} · ${resource.metadata.title ?? resource.metadata.name} · ${resource.spec.type}`
                : alert.resourceId;
              return (
                <div key={alert.id} className={styles.alItem}>
                  <div className={styles.alText}>
                    <span className={styles.nm}>{title}</span>
                    <span className={styles.alRes} title={alert.resourceId}>
                      {resLine}
                    </span>
                  </div>
                  <span className={styles.when}>{relTime(alert.created_at)}</span>
                </div>
              );
            })}
            {recentAlerts.length === 0 && (
              <div className={styles.cardSub}>No recent alerts</div>
            )}
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Insights
            <Link className={styles.go} to={`${accountHref}/insights?st=Active`}>
              Open Insights →
            </Link>
          </div>
          {activeFindings !== undefined && (
            <div className={styles.insRow}>
              <span
                className={`${styles.insRowOk} ${activeFindings === 0 ? '' : styles.insRowOkWarn}`}
              >
                {activeFindings === 0 ? '✓' : '!'}
              </span>
              <span className={styles.nm}>
                External access analyzers
                <div className={styles.sub2}>
                  {activeFindings === 0
                    ? 'No resources exposed to the internet found'
                    : `${activeFindings} active finding${activeFindings > 1 ? 's' : ''}`}
                </div>
              </span>
            </div>
          )}
          {activeFindings === undefined && (
            <div className={styles.cardSub}>No analyzer data yet</div>
          )}
        </div>
      </div>
    </>
  );
};

export const AccountOverviewTab = () => {
  const { name } = useRouteRefParams(entityRouteRef);

  return (
    <RulesContextProvider accountName={name}>
      <AccountOverviewTabContent />
    </RulesContextProvider>
  );
};
